import { Box, Image, useColorModeValue, chakra } from '@chakra-ui/react';
import React from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import { useExplorerConfig } from 'lib/contexts/ExplorerConfigContext';
import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';

interface Props {
  isCollapsed?: boolean;
  onClick?: (event: React.SyntheticEvent) => void;
  className?: string;
}

const LogoFallback = ({ isCollapsed, isSmall }: { isCollapsed?: boolean; isSmall?: boolean }) => {
  const field = isSmall ? 'icon' : 'logo';
  const logoColor = useColorModeValue('blue.600', 'white');

  const display = isSmall ? {
    base: 'none',
    lg: isCollapsed === false ? 'none' : 'block',
    xl: isCollapsed ? 'block' : 'none',
  } : {
    base: 'block',
    lg: isCollapsed === false ? 'block' : 'none',
    xl: isCollapsed ? 'none' : 'block',
  };

  if (config.UI.navigation[field].default) {
    return <Skeleton w="100%" borderRadius="sm" display={ display }/>;
  }

  return (
    <IconSvg
      name={ isSmall ? 'networks/icon-placeholder' : 'networks/logo-placeholder' }
      width="auto"
      height="100%"
      color={ logoColor }
      display={ display }
    />
  );
};

const NetworkLogo = ({ isCollapsed, onClick, className }: Props) => {
  const { config: explorerConfig } = useExplorerConfig();

  // Use admin-set logo if available, otherwise fall back to env config
  const adminLogoLight = explorerConfig?.branding?.logo_light_url;
  const adminLogoDark = explorerConfig?.branding?.logo_dark_url;

  const defaultLogoSrc = useColorModeValue(config.UI.navigation.logo.default, config.UI.navigation.logo.dark || config.UI.navigation.logo.default);
  const defaultIconSrc = useColorModeValue(config.UI.navigation.icon.default, config.UI.navigation.icon.dark || config.UI.navigation.icon.default);

  // Admin logos take priority over env config logos
  const logoSrc = useColorModeValue(
    adminLogoLight || defaultLogoSrc,
    adminLogoDark || adminLogoLight || (config.UI.navigation.logo.dark || config.UI.navigation.logo.default),
  );
  const iconSrc = defaultIconSrc;

  const darkModeFilter = { filter: 'brightness(0) invert(1)' };
  // If admin logo is set, don't apply dark mode filter
  const hasAdminLogo = Boolean(adminLogoLight || adminLogoDark);
  const logoStyle = useColorModeValue({}, hasAdminLogo ? {} : (!config.UI.navigation.logo.dark ? darkModeFilter : {}));
  const iconStyle = useColorModeValue({}, !config.UI.navigation.icon.dark ? darkModeFilter : {});

  return (
    <Box
      className={ className }
      as="a"
      href={ route({ pathname: '/' }) }
      width={{ base: '200px', lg: isCollapsed === false ? '240px' : '40px', xl: isCollapsed ? '40px' : '240px' }}
      height={{ base: '48px', lg: isCollapsed === false ? '52px' : '40px', xl: isCollapsed ? '40px' : '52px' }}
      display="inline-flex"
      overflow="hidden"
      onClick={ onClick }
      flexShrink={ 0 }
      aria-label="Link to main page"
    >
      { /* big logo */ }
      <Image
        w="100%"
        h="100%"
        src={ logoSrc }
        alt={ `${ config.chain.name } network logo` }
        fallback={ <LogoFallback isCollapsed={ isCollapsed }/> }
        display={{ base: 'block', lg: isCollapsed === false ? 'block' : 'none', xl: isCollapsed ? 'none' : 'block' }}
        style={ { ...(logoStyle), objectFit: 'contain' } }
      />
      { /* small logo */ }
      <Image
        w="100%"
        h="100%"
        src={ iconSrc }
        alt={ `${ config.chain.name } network logo` }
        fallback={ <LogoFallback isCollapsed={ isCollapsed } isSmall/> }
        display={{ base: 'none', lg: isCollapsed === false ? 'none' : 'block', xl: isCollapsed ? 'block' : 'none' }}
        style={ { ...(iconStyle), objectFit: 'contain' } }
      />
    </Box>
  );
};

export default React.memo(chakra(NetworkLogo));
